import { IModuleApi, Lesson } from 'src/service/api/api.types'
import React from 'react'
import {
  Module,
  SubMenu,
  Title,
} from 'src/components/Common/MenuModules/components/ModuleList/styles'
import { FaCheckCircle } from 'react-icons/fa'
import { reduceProgressType } from 'src/utils/calcProgress'
import { useTranslation } from 'react-i18next'
import { setModuleActive } from 'src/store/lessons'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'src/store/rootReducers'
import { useRouter } from 'next/router'

interface Props {
  module: IModuleApi
  identify: number
  progress: reduceProgressType
}

const ModuleItem: React.FC<Props> = ({ module, identify, progress }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const router = useRouter()
  const { classroom } = router.query
  const moduleActive = useSelector((state: State) => state.course.moduleActive)

  const [itemActive, setItemActive] = React.useState<number | null>(null)
  const lessonVisible = module.lessons.some(item => item.slug === classroom)

  const subItemArea = React.useRef<HTMLDivElement | null>(null)

  const statuses = {
    in_progress: 'in-progress',
    completed: 'completed',
    pending: 'pending',
  }
  const getStatus = React.useCallback(
    (calc: number, lessonsTotal) => {
      if (calc > 0 && calc < lessonsTotal) {
        return statuses.in_progress
      }

      if (calc === lessonsTotal) {
        return statuses.completed
      }

      return statuses.pending
    },
    [progress],
  )

  React.useEffect(() => {
    if (classroom) {
      if (!lessonVisible && moduleActive !== module.id) {
        setItemActive(null)
        return
      }

      setItemActive(identify)
      dispatch(setModuleActive(module.id))
    }
  }, [classroom, moduleActive])

  async function fetchLesson(lesson: Lesson) {
    await router.push(`/curso/aula/${lesson.slug}`)
  }

  const calcHeightLessonArea = React.useCallback((lessons: HTMLDivElement[]) => lessons.reduce((acc, current: HTMLDivElement) => {
      acc += current.offsetHeight
      return acc
    }, 0), [subItemArea, itemActive]);

  React.useEffect(() => {
    if (subItemArea && subItemArea.current) {
      const lessons = Array.from(subItemArea.current.querySelectorAll('.subItem')) as HTMLDivElement[]
      if (lessons && lessons.length > 0) {
        const totalHeight = calcHeightLessonArea(lessons)

        if(subItemArea.current.classList.contains('opened')) {
          subItemArea.current.style.maxHeight = `${totalHeight}px`;
        } else {
          subItemArea.current.style.maxHeight = '0';

        }
      }
    }
  }, [subItemArea, itemActive])

  const renderLessons = (lessons: Lesson[]) =>
    lessons?.map(lesson => (
      <SubMenu
        key={lesson.id}
        onClick={() => fetchLesson(lesson)}
        className={`${lesson.slug === classroom ? 'lesson-visible' : ''} subItem`}
      >
        <Title className="classroom">
          <FaCheckCircle className={`icon ${lesson.wasWatched ? 'visa' : ''}`} />
          <span>{lesson.title}</span>
        </Title>
      </SubMenu>
    ))

  return (
    <>
      <Module
        onClick={() => {
          if (itemActive === identify && !lessonVisible && moduleActive !== module.id) {
            setItemActive(null)
            return
          }

          setItemActive(identify)
        }}
      >
        <Title>
          <span className="span-module">
            <i className={getStatus(progress.progress, progress.lessons)}>
              <FaCheckCircle className="icon-module visa" />
            </i>
            <span className="module-text">{module.title}</span>
          </span>
        </Title>

        <span className="topic">
          {t('classroom.lessonsCounter', { lessonsTotal: module.lessons.length })}
        </span>
      </Module>

      <div
        ref={subItemArea}
        className={`nav ${itemActive === identify ? 'opened' : 'closed'}`}
      >
        {module.lessons && renderLessons(module.lessons)}
      </div>
    </>
  )
}

export default React.memo(ModuleItem)
